import React from "react";

export default function FullscreenLoader({
  logoSrc = "/kataloji_512x512.png",
  alt = "Kataloji",
  /** Optional line under the logo. */
  hint = "",
  /** Color for the hint line under the logo. */
  hintColor = "#111827",
  /** Thin indeterminate progress bar under the hint. */
  showProgressBar = false,
  /** Progress bar fill color (defaults to Kataloji yellow). */
  progressBarColor = "",
  /** Fits inside modals, tables, and dashboard panels (not full viewport). */
  embedded = false,
  /** Override min-height when `embedded` (CSS length). */
  minHeight,
  /** Plain white page — used while waiting for store logo (no Kataloji flash). */
  blankUntilLogo = false,
}) {
  const label = String(hint || "").trim();
  const showBar = Boolean(showProgressBar);
  const labelColor = String(hintColor || "").trim() || "#111827";
  const barColor = String(progressBarColor || "").trim() || "#eab308";
  const logo = String(logoSrc || "").trim();

  const rootStyle = embedded
    ? {
        minHeight: minHeight || "220px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        background: "transparent",
        position: "relative",
      }
    : {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#ffffff",
        position: "relative",
        overflow: "hidden",
      };

  const logoStyle = {
    display: "block",
    margin: "0 auto",
    width: "auto",
    height: "auto",
    maxWidth: embedded ? "min(96px, 22vw)" : "min(160px, 42vw)",
    maxHeight: embedded ? "min(96px, 22vw)" : "min(160px, 42vw)",
    objectFit: "contain",
    animation: "katalojiPulseSoft 1.6s ease-in-out infinite",
  };

  return (
    <div
      className={embedded ? "kataloji-loader kataloji-loader--embedded" : "kataloji-loader"}
      style={rootStyle}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div style={{ textAlign: "center", padding: "0 20px", maxWidth: "100%" }}>
        {logo ? (
          <img src={logo} alt={alt} width={512} height={512} decoding="async" style={logoStyle} />
        ) : (
          <div
            aria-hidden
            style={{
              width: embedded ? 72 : 96,
              height: embedded ? 72 : 96,
              margin: "0 auto",
              borderRadius: "50%",
              background: "rgba(148, 163, 184, 0.15)",
            }}
          />
        )}
        {label ? (
          <p
            dir="auto"
            style={{
              margin: embedded ? "14px 0 0" : "18px 0 0",
              fontSize: embedded ? 14 : 15,
              fontWeight: 800,
              color: labelColor,
            }}
          >
            {label}
          </p>
        ) : null}
        {showBar ? (
          <div
            aria-hidden
            style={{
              margin: embedded ? "16px auto 0" : "22px auto 0",
              maxWidth: 280,
              height: 4,
              borderRadius: 999,
              background: "rgba(148, 163, 184, 0.22)",
              overflow: "hidden",
            }}
          >
            <div
              className="kataloji-loader-progress"
              style={{
                background: `linear-gradient(90deg, ${barColor}, ${barColor})`,
              }}
            />
          </div>
        ) : null}
      </div>

      <style>{`
        @keyframes katalojiPulseSoft {
          0%, 100% { transform: scale(0.99); opacity: 0.92; }
          50% { transform: scale(1.01); opacity: 1; }
        }
        @keyframes katalojiLoaderBar {
          0% { transform: translateX(-100%); width: 35%; }
          50% { transform: translateX(40%); width: 45%; }
          100% { transform: translateX(220%); width: 35%; }
        }
        .kataloji-loader-progress {
          height: 100%;
          border-radius: 999px;
          animation: katalojiLoaderBar 1.35s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .kataloji-loader-progress { animation: none; width: 40%; margin: 0 auto; transform: none; }
        }
      `}</style>
    </div>
  );
}
